// jobtypes/form/jobtypes-form-page.tsx
import React, { useEffect, useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupStringReq,
  promiseModalDialog,
  toastSuccess,
  toastWarning,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { useTranslation } from 'react-i18next';
import { getRouteByName } from 'app/routes';
import { useHandleError } from 'shared-pages/services';

import { FormFields } from './form-fields';
import { gqlGetJobtypeById, gqlAddJobtype, gqlUpdateJobtype, gqlDeleteJobtype } from '../graphql';

export const JobtypesFormPage = () => {
  const { handleGraphqlError } = useHandleError('JobtypesFormPage');
  const history = useHistory();
  const location: any = useLocation();
  const { id: rawId } = useParams<{ id: string }>();
  const n = Number(rawId);
  const isNew = rawId === 'new' || !Number.isFinite(n) || n <= 0;
  const idNum = isNew ? 0 : n;

  const { t } = useTranslation();

  // ---- Validation ----
  const formShape = {
    id: YupNumeric,
    code: YupStringReq,
    description: YupStringReq,
    // materialId is optional; other text fields optional too
  };
  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values: any) => Lo.pick(values, Lo.keys(formShape));

  type FormValuesType = {
    id: number | null;
    code: string;
    description: string;
    detailedDescription?: string;
    chargingCode?: string;
    materialId: number | null;
    isActive: boolean;
  };

  // ---- Mutations ----
  const [createRecord] = useMutation(gqlAddJobtype, {
    onError: (e) => toastWarning(e.message),
  });

  const [updateRecord] = useMutation(gqlUpdateJobtype, {
    onError: (e) => toastWarning(e.message),
  });

  const [deleteRecord] = useMutation(gqlDeleteJobtype, {
    onError: (e) => toastWarning(e.message),
  });

  // ---- Query (fetch single by id via where) ----
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetJobtypeById, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!isNew) get({ variables: { id: idNum } });
  }, [get, idNum, isNew]);

  const data = rawData?.getJobtypeById;
  const base = data ? (pickProperties(data) as any) : { id: null, code: '', description: '' };

  const initialValues: FormValuesType = {
    ...base,
    detailedDescription: data?.detailedDescription ?? '',
    chargingCode: data?.chargingCode ?? '',
    materialId: data?.materialId ?? null,
    isActive: data?.isActive ?? true,
  };

  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: t('Διαγραφή Εργασίας'),
      text: t('Είστε σίγουροι ότι θέλετε να διαγράψετε την Εργασία;'),
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => {
          toastSuccess(`Επιτυχής Διαγραφή Εργασίας [${idNum}]`);
          history.goBack();
        })
        .catch((err) => handleGraphqlError('mutation-delete', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Jobtypes')), { ...location.state });
  }, [history, location.state]);

  const handleSubmit = async (values: FormValuesType, actions: any) => {
    try {
      const common = {
        code: String(values.code ?? '').trim(),
        description: String(values.description ?? '').trim(),
        detailedDescription: String(values.detailedDescription ?? '').trim() || null,
        chargingCode: String(values.chargingCode ?? '').trim() || null,
        materialId:
          values.materialId === null || values.materialId === undefined
            ? null
            : Number(values.materialId),
        isActive: typeof values.isActive === 'boolean' ? values.isActive : true,
      };

      if (isNew) {
        const { data: m } = await createRecord({ variables: { data: common } });
        const createdId = m?.addJobtype?.id;
        if (!createdId) throw new Error('Create failed');

        toastSuccess(`Επιτυχής Δημιουργία [${createdId}]`);
        actions.setSubmitting(false);

        history.replace(generatePath(getRouteByName('JobtypesForm'), { id: createdId }), {
          ...location.state,
        });
        return;
      }

      // UPDATE
      const payload = { id: idNum, ...common };
      const changed =
        payload.code !== (data?.code ?? '') ||
        payload.description !== (data?.description ?? '') ||
        (payload.detailedDescription ?? '') !== (data?.detailedDescription ?? '') ||
        (payload.chargingCode ?? '') !== (data?.chargingCode ?? '') ||
        (payload.materialId ?? null) !== (data?.materialId ?? null) ||
        payload.isActive !== (data?.isActive ?? true);

      if (!changed) throw new Error('Δεν υπάρχουν αλλαγές');

      const { data: m } = await updateRecord({
        variables: { data: payload },
        refetchQueries: [{ query: gqlGetJobtypeById, variables: { id: idNum } }],

        awaitRefetchQueries: true,
      });

      if (!m?.updateJobtype?.id) throw new Error('Update failed');
      toastSuccess(`${t('Επιτυχής Ενημέρωση Εγγραφής')} [${m.updateJobtype.id}]`);
    } catch (err: any) {
      toastWarning(err?.message ?? 'Mutation failed');
      actions.setSubmitting(false);
    } finally {
      if (!isNew) actions.setSubmitting(false);
    }
  };

  if (error) {
    return <h3 className="text-center">{error?.message}</h3>;
  }

  return (
    <FormCard
      title={`${t('Εργασία')}: ${data?.code || initialValues.code || '-'}`}
      onSubmit={handleSubmit}
      onDelete={isNew ? undefined : handleDelete}
      onReturn={handleReturn}
      permitSubmit={false}
      permitDelete={true}
      noButtons={false}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      idx={idNum}
    >
      <FormFields
        updateRecord={updateRecord}
        createRecord={createRecord}
        deleteRecord={deleteRecord}
        refetch={refetch}
        data={data}
        isNew={isNew}
      />
    </FormCard>
  );
};
